import { useState } from "react";

// Illustrative content. The RAID example was changed on 2026-09-28 from a Cyber Security Uplift
// initiative to a council service redesign, to suit the site's government and general PM audience;
// the ERP and Contact Centre examples are flavoured from the demo initiatives seeded in the app.
// Static and client-side only: the real data lives behind auth in production Supabase and
// isn't something this public page can safely pull live.

const PILL_TONES = {
  neutral: { fg: "var(--text-secondary)", bg: "var(--surface-2)" },
  green: { fg: "var(--green)", bg: "var(--green-light)" },
  amber: { fg: "var(--amber)", bg: "var(--amber-light)" },
  red: { fg: "#EF4444", bg: "#FEE2E2" },
};

function Pill({ children, tone = "neutral" }) {
  const { fg, bg } = PILL_TONES[tone];
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.02em",
        padding: "2px 9px",
        borderRadius: 20,
        color: fg,
        background: bg,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

const RAID_ITEMS = [
  {
    id: "risk1",
    type: "Risk",
    typeTone: "neutral",
    title: "Frontline service capacity strain during the online request system rollout",
    statusLabel: "High · Open",
    statusTone: "amber",
    detail: "Mitigation: Phase the rollout by service area, avoiding the rates-notice peak; roster two experienced officers as floor support for the first fortnight of each wave.",
  },
  {
    id: "dec1",
    type: "Decision",
    typeTone: "neutral",
    title: "Approve the consolidated online request form for council services",
    statusLabel: "Approved",
    statusTone: "green",
    detail: "Notes: Chosen after testing with residents and frontline staff; accessibility and fewer handoffs outweighed a slightly faster build from the alternative option.",
  },
  {
    id: "issue1",
    type: "Issue",
    typeTone: "neutral",
    title: "Service records migration behind schedule due to inconsistent request categories across departments",
    statusLabel: "Extreme · Open",
    statusTone: "red",
    detail: "Mitigation: A working group is mapping the legacy categories by hand; RTG raised to formally reset the recovery plan.",
  },
];

function PmDemo() {
  const [openId, setOpenId] = useState(RAID_ITEMS[0].id);
  return (
    <div style={{ padding: "1.25rem 1.5rem" }}>
      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-muted)", margin: "0 0 12px" }}>
        RAID Register: Council Service Redesign
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {RAID_ITEMS.map((item) => {
          const open = openId === item.id;
          return (
            <div key={item.id} style={{ border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
              <button
                onClick={() => setOpenId(open ? null : item.id)}
                aria-expanded={open}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "12px 14px",
                  background: open ? "var(--surface-1)" : "var(--surface-0)",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <Pill tone={item.typeTone}>{item.type}</Pill>
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", flex: "1 1 240px" }}>{item.title}</span>
                <Pill tone={item.statusTone}>{item.statusLabel}</Pill>
              </button>
              {open && (
                <div style={{ padding: "0 14px 14px", fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.6 }}>
                  {item.detail}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CM_GATES = [
  { id: "g1", label: "Initiation → Planning", status: "SME baseline set" },
  { id: "g2", label: "Planning → Delivery", status: "Team responses + Strategy drafted" },
  {
    id: "g3",
    label: "Training & Readiness Gate",
    status: "Training Plan drafted",
    extra: "Finance team change fatigue building after two delayed go-live dates. Direct communication from the CFO is built into the recovery plan.",
  },
  { id: "g4", label: "Pre-go-live resistance gate", status: "Not triggered" },
];

function CmDemo() {
  const [activeId, setActiveId] = useState("g3");
  const active = CM_GATES.find((g) => g.id === activeId);
  return (
    <div style={{ padding: "1.25rem 1.5rem" }}>
      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-muted)", margin: "0 0 12px" }}>
        Change Readiness Gates: ERP Transformation
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {CM_GATES.map((g) => {
          const isActive = g.id === activeId;
          return (
            <button
              key={g.id}
              onClick={() => setActiveId(g.id)}
              aria-pressed={isActive}
              style={{
                padding: "8px 14px",
                borderRadius: 20,
                fontSize: 12.5,
                fontWeight: 600,
                border: isActive ? "1px solid var(--lilac-deep)" : "1px solid var(--border-strong)",
                background: isActive ? "var(--lilac-wash)" : "var(--surface-0)",
                color: isActive ? "var(--lilac-deep)" : "var(--text-secondary)",
                cursor: "pointer",
              }}
            >
              {g.label}
            </button>
          );
        })}
      </div>
      <div style={{ background: "var(--surface-1)", borderRadius: "var(--radius)", padding: "14px 16px" }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", margin: active.extra ? "0 0 8px" : 0 }}>{active.status}</p>
        {active.extra && (
          <p style={{ fontSize: 12.5, color: "var(--text-secondary)", margin: 0, lineHeight: 1.6 }}>{active.extra}</p>
        )}
      </div>
    </div>
  );
}

const DMAIC_PHASES = [
  { id: "define", label: "Define", blurb: "Average call handling time up 22% over 18 months; first-contact resolution down to 61%, well below the 80% target." },
  { id: "measure", label: "Measure", blurb: "Baseline measured: 61% first-contact resolution, agents searching across 4 disconnected knowledge sources mid-call." },
  { id: "analyse", label: "Analyse", blurb: "5-Whys root cause workshop identified knowledge fragmentation as the primary driver." },
  { id: "improve", label: "Improve", blurb: "Consolidated knowledge base and redesigned escalation workflow, piloted with a 12-agent team." },
  { id: "control", label: "Control", blurb: "Pilot already showing a 9% handling-time reduction and 74% first-contact resolution, tracking toward target." },
];

function BiDemo() {
  const [activeId, setActiveId] = useState("analyse");
  const active = DMAIC_PHASES.find((p) => p.id === activeId);
  const activeIdx = DMAIC_PHASES.findIndex((p) => p.id === activeId);
  return (
    <div style={{ padding: "1.25rem 1.5rem" }}>
      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-muted)", margin: "0 0 12px" }}>
        DMAIC Cycle: Contact Centre Service Improvement
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {DMAIC_PHASES.map((p) => {
          const isActive = p.id === activeId;
          return (
            <button
              key={p.id}
              onClick={() => setActiveId(p.id)}
              aria-pressed={isActive}
              style={{
                flex: 1,
                padding: "10px 6px",
                borderRadius: 8,
                textAlign: "center",
                fontSize: 12,
                fontWeight: 600,
                border: "1px solid var(--border)",
                background: isActive ? "var(--lilac-deep)" : "var(--surface-1)",
                color: isActive ? "#fff" : "var(--navy)",
                cursor: "pointer",
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      <div style={{ background: "var(--surface-1)", borderRadius: "var(--radius)", padding: "14px 16px" }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: "var(--lilac-deep)", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Phase {activeIdx + 1} of 5
        </p>
        <p style={{ fontSize: 13, color: "var(--text-primary)", margin: 0, lineHeight: 1.6 }}>{active.blurb}</p>
      </div>
    </div>
  );
}

export default function PillarInteractiveDemo({ pillarId }) {
  if (pillarId === "pm") return <PmDemo />;
  if (pillarId === "cm") return <CmDemo />;
  if (pillarId === "bi") return <BiDemo />;
  return null;
}
